import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { downloadBlob } from '../lib/download';
import { toast } from '../stores/toaster';
import type { Annotation, AnnotationCategory, Bookmark, PdfMetadata } from '../lib/types';

/**
 * Onglet « Dossier de travail » (Sprint 2 §9).
 *
 * Vue de lecture du Dossier de travail = ensemble des marqueurs (bookmarks)
 * poses dans les pieces du dossier, regroupes par piece, avec lien retour
 * vers le viewer et export PDF du mini-dossier.
 *
 * Les marqueurs sur pieces client (piece_client_id) sont ignores ici —
 * /export-bookmarks fait pareil. A revisiter si on enrichit les pieces
 * client avec un viewer dedie.
 */

type Props = {
  dossierId: string;
  ref_cabinet: string | null | undefined;
  bookmarks: Bookmark[];
  pdfs: PdfMetadata[];
  /** Sprint F3 (2026-05-23) — annotations highlightées affichées en
   * chips colorées sous chaque page/pièce bookmarkée (le « fluo »). */
  annotations?: Annotation[];
  onChange: () => Promise<void> | void;
};

type Group = {
  pdf: PdfMetadata;
  pageBookmarks: Bookmark[];
  wholePiece: Bookmark | null;
};

/** Couleurs des chips highlight par catégorie — match les pastel CSS
 *  du viewer pour cohérence visuelle. */
const CATEGORY_STYLES: Record<AnnotationCategory, string> = {
  favorable: 'bg-cat-fav-pastel text-cat-fav',
  defavorable: 'bg-cat-def-pastel text-cat-def',
  important: 'bg-cat-imp-pastel text-cat-imp-text',
  a_verifier: 'bg-cat-ver-pastel text-cat-ver',
};
const CATEGORY_LABELS: Record<AnnotationCategory, string> = {
  favorable: 'Fav',
  defavorable: 'Déf',
  important: 'Imp',
  a_verifier: '?',
};

export default function MiniDossierTab({
  dossierId,
  ref_cabinet,
  bookmarks,
  pdfs,
  annotations = [],
  onChange,
}: Props) {
  const [exporting, setExporting] = useState(false);
  const [busyDelete, setBusyDelete] = useState<string | null>(null);

  // Index annotations par pdf_file_id × page_number pour O(1) lookup.
  // Une pièce entière bookmarkée → fluo = toutes les annotations de la pièce.
  const annotsByPdfPage = useMemo(() => {
    const m = new Map<string, Annotation[]>();
    for (const a of annotations) {
      if (!a.pdf_file_id) continue;
      const key = `${a.pdf_file_id}:${a.page_number}`;
      const arr = m.get(key) ?? [];
      arr.push(a);
      m.set(key, arr);
    }
    return m;
  }, [annotations]);
  const annotsByPdf = useMemo(() => {
    const m = new Map<string, Annotation[]>();
    for (const a of annotations) {
      if (!a.pdf_file_id) continue;
      const arr = m.get(a.pdf_file_id) ?? [];
      arr.push(a);
      m.set(a.pdf_file_id, arr);
    }
    return m;
  }, [annotations]);

  const pdfById = useMemo(() => {
    const m = new Map<string, PdfMetadata>();
    for (const p of pdfs) m.set(p.id, p);
    return m;
  }, [pdfs]);

  const groups = useMemo<Group[]>(() => {
    const m = new Map<string, Group>();
    for (const b of bookmarks) {
      if (!b.pdf_file_id) continue;
      const pdf = pdfById.get(b.pdf_file_id);
      if (!pdf) continue;
      const g = m.get(pdf.id) ?? { pdf, pageBookmarks: [], wholePiece: null };
      if (b.page_number === null) {
        g.wholePiece = b;
      } else {
        g.pageBookmarks.push(b);
      }
      m.set(pdf.id, g);
    }
    const arr = Array.from(m.values());
    for (const g of arr) {
      g.pageBookmarks.sort((a, b) => (a.page_number ?? 0) - (b.page_number ?? 0));
    }
    arr.sort((a, b) => a.pdf.filename_original.localeCompare(b.pdf.filename_original));
    return arr;
  }, [bookmarks, pdfById]);

  const totalPageBookmarks = groups.reduce((s, g) => s + g.pageBookmarks.length, 0);
  const totalWhole = groups.filter((g) => g.wholePiece).length;

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadBlob(
        `/api/v1/dossiers/${dossierId}/export-bookmarks`,
        `mini-dossier-${ref_cabinet ?? 'adr'}.pdf`
      );
      toast.success('Mini-dossier PDF exporté');
    } catch (err) {
      toast.error((err as Error).message || 'Erreur lors de l’export');
    } finally {
      setExporting(false);
    }
  };

  const removeBookmark = async (b: Bookmark) => {
    if (!window.confirm('Retirer ce marqueur ?')) return;
    setBusyDelete(b.id);
    try {
      await api.delete(`/bookmarks/${b.id}`);
      await onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusyDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-paper-elevated p-3">
        <div className="text-[12px] text-text-secondary">
          {groups.length === 0 ? (
            <span>Aucun marqueur dans ce dossier.</span>
          ) : (
            <>
              <strong className="font-medium text-text-primary">{groups.length}</strong> pièce
              {groups.length > 1 ? 's' : ''} marquée{groups.length > 1 ? 's' : ''}
              {' · '}
              <strong className="font-medium text-text-primary">{totalPageBookmarks}</strong> page
              {totalPageBookmarks > 1 ? 's' : ''}
              {totalWhole > 0 && (
                <>
                  {' · '}
                  <strong className="font-medium text-text-primary">{totalWhole}</strong> pièce
                  {totalWhole > 1 ? 's' : ''} entière{totalWhole > 1 ? 's' : ''}
                </>
              )}
            </>
          )}
        </div>
        <Btn
          variant="primary"
          size="sm"
          onClick={() => void exportPdf()}
          disabled={exporting || groups.length === 0}
          title="Génère un PDF avec inventaire en page 1 puis pages réelles extraites"
        >
          {exporting ? 'Export…' : '📦 Exporter en PDF'}
        </Btn>
      </div>

      {groups.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucun marqueur. Ouvre une pièce, puis utilise les boutons « 🏷 Page » ou « 🏷 Pièce » dans
          la barre d&apos;outils du viewer pour constituer ton dossier de travail.
        </p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <section
              key={g.pdf.id}
              className="overflow-hidden rounded-md border border-rule bg-paper-elevated"
            >
              <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule bg-paper-card px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/dossiers/${dossierId}/pieces/${g.pdf.id}`}
                    className="font-serif text-[14px] font-medium text-text-primary no-underline hover:underline"
                  >
                    {g.pdf.titre_pv || g.pdf.filename_original}
                  </Link>
                  {g.pdf.numero_pv && (
                    <span className="ml-2 font-mono text-[11px] text-text-tertiary">
                      PV n° {g.pdf.numero_pv}
                    </span>
                  )}
                </div>
                <span className="font-mono text-[11px] text-text-tertiary">
                  {g.pageBookmarks.length} page
                  {g.pageBookmarks.length > 1 ? 's' : ''}
                  {g.wholePiece ? ' · pièce entière' : ''}
                </span>
              </header>
              <ul className="divide-y divide-rule-soft">
                {g.wholePiece && (
                  <li className="px-4 py-2 text-[13px]">
                    <div className="flex items-center justify-between gap-3">
                      <Link
                        to={`/dossiers/${dossierId}/pieces/${g.pdf.id}`}
                        className="flex-1 text-text-primary no-underline hover:underline"
                      >
                        🏷★ <span className="font-medium">Pièce entière</span>
                        {g.pdf.page_count && (
                          <span className="ml-2 font-mono text-[11px] text-text-tertiary">
                            {g.pdf.page_count} page{g.pdf.page_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </Link>
                      <span className="font-mono text-[11px] text-text-tertiary">
                        {g.wholePiece.creator_display_name ?? '—'} ·{' '}
                        {new Date(g.wholePiece.created_at).toLocaleDateString('fr-BE')}
                      </span>
                      <button
                        type="button"
                        onClick={() => void removeBookmark(g.wholePiece!)}
                        disabled={busyDelete === g.wholePiece.id}
                        className="rounded p-0.5 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def disabled:opacity-50"
                        title="Retirer ce marqueur"
                        aria-label="Retirer le marqueur de cette pièce entière"
                      >
                        🗑
                      </button>
                    </div>
                    <HighlightChips
                      annotations={annotsByPdf.get(g.pdf.id) ?? []}
                      dossierId={dossierId}
                      pdfId={g.pdf.id}
                    />
                  </li>
                )}
                {g.pageBookmarks.map((b) => (
                  <li
                    key={b.id}
                    className="px-4 py-2 text-[13px]"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <Link
                        to={`/dossiers/${dossierId}/pieces/${g.pdf.id}?page=${b.page_number}`}
                        className="flex-1 text-text-primary no-underline hover:underline"
                      >
                        🏷 <span className="font-medium">Page {b.page_number}</span>
                      </Link>
                      <span className="font-mono text-[11px] text-text-tertiary">
                        {b.creator_display_name ?? '—'} ·{' '}
                        {new Date(b.created_at).toLocaleDateString('fr-BE')}
                      </span>
                      <button
                        type="button"
                        onClick={() => void removeBookmark(b)}
                        disabled={busyDelete === b.id}
                        className="rounded p-0.5 text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def disabled:opacity-50"
                        title="Retirer ce marqueur"
                        aria-label={`Retirer le marqueur de la page ${b.page_number}`}
                      >
                        🗑
                      </button>
                    </div>
                    <HighlightChips
                      annotations={annotsByPdfPage.get(`${g.pdf.id}:${b.page_number ?? 0}`) ?? []}
                      dossierId={dossierId}
                      pdfId={g.pdf.id}
                      pageNumber={b.page_number}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Affiche les annotations highlight d'une page/pièce sous forme de chips
 * colorées avec extrait texte (sprint F3 fluo mini-dossier). Click =
 * ouvre la page concernée dans le viewer + flash sur le highlight.
 */
function HighlightChips({
  annotations,
  dossierId,
  pdfId,
  pageNumber,
}: {
  annotations: Annotation[];
  dossierId: string;
  pdfId: string;
  pageNumber?: number | null;
}) {
  if (annotations.length === 0) return null;
  // Tri par page (utile pour "Pièce entière") puis par y-coord pour
  // refléter l'ordre de lecture vertical.
  const sorted = [...annotations].sort((a, b) => {
    if (a.page_number !== b.page_number) return a.page_number - b.page_number;
    const ya = a.coords_json?.[0]?.y ?? 0;
    const yb = b.coords_json?.[0]?.y ?? 0;
    return ya - yb;
  });
  return (
    <ul className="mt-1.5 flex flex-col gap-1 pl-5">
      {sorted.map((a) => (
        <li key={a.id} className="text-[11.5px]">
          <Link
            to={`/dossiers/${dossierId}/pieces/${pdfId}?page=${a.page_number}&annotation=${a.id}`}
            className="inline-flex items-start gap-1.5 no-underline hover:underline"
            title={a.comment ?? undefined}
          >
            <span
              className={`shrink-0 rounded-pill px-1.5 py-[1px] text-[9.5px] font-semibold uppercase tracking-wider ${CATEGORY_STYLES[a.category]}`}
            >
              {CATEGORY_LABELS[a.category]}
            </span>
            {pageNumber == null && a.page_number != null && (
              <span className="shrink-0 font-mono text-[10px] text-text-tertiary">
                p.{a.page_number}
              </span>
            )}
            <span className="line-clamp-2 italic text-text-secondary">
              « {a.text_extract} »
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
